import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Cookie } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { getConsent, saveConsent } from "@/lib/consent";

export const CookieConsent = () => {
  const [banner, setBanner] = useState(false);
  const [manage, setManage] = useState(false);
  const [analytics, setAnalytics] = useState(false);

  useEffect(() => {
    const c = getConsent();
    if (!c) setBanner(true);
    else setAnalytics(!!c.analytics);
    const open = () => setManage(true);
    window.addEventListener("lc:cookie-settings", open);
    return () => window.removeEventListener("lc:cookie-settings", open);
  }, []);

  const save = (a) => {
    saveConsent(a);
    setAnalytics(a);
    setBanner(false);
    setManage(false);
  };

  return (
    <>
      {banner && !manage && (
        <div role="region" aria-label="Cookie consent" data-testid="cookie-banner"
          className="fixed bottom-24 left-4 right-4 z-[60] max-w-md rounded-2xl border border-[#2E2E2E] bg-[#121212]/95 p-5 shadow-2xl backdrop-blur-xl lg:bottom-6 lg:left-6 lg:right-auto">
          <div className="flex items-start gap-3">
            <Cookie className="mt-0.5 h-5 w-5 shrink-0 text-[#D4A437]" />
            <p className="text-sm leading-relaxed text-[#F7F5F0]/80">
              We use essential cookies to run this site. With your permission we'd also like to use analytics cookies to improve it.{" "}
              <Link to="/cookie-policy" className="underline underline-offset-2 hover:text-[#FF6A00]">Cookie policy</Link>
            </p>
          </div>
          <div className="mt-4 flex flex-wrap gap-2">
            <button onClick={() => save(true)} className="lc-btn-primary !px-4 !py-2.5" data-testid="cookie-accept-all">Accept all</button>
            <button onClick={() => save(false)} className="lc-btn-ghost !px-4 !py-2.5" data-testid="cookie-reject">Essential only</button>
            <button onClick={() => setManage(true)} className="px-3 text-sm text-[#A1A1AA] underline-offset-2 hover:underline" data-testid="cookie-manage">Manage</button>
          </div>
        </div>
      )}
      <Dialog open={manage} onOpenChange={setManage}>
        <DialogContent className="border-[#2E2E2E] bg-[#121212] sm:rounded-2xl" data-testid="cookie-settings-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Cookie settings</DialogTitle>
            <DialogDescription>Choose which cookies we may use. You can change this at any time.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="flex items-center justify-between rounded-xl border border-[#2E2E2E] p-4">
              <div>
                <p className="font-medium">Essential</p>
                <p className="text-sm text-[#A1A1AA]">Needed for checkout, sign-in and security.</p>
              </div>
              <Switch checked disabled aria-label="Essential cookies (always on)" />
            </div>
            <div className="flex items-center justify-between rounded-xl border border-[#2E2E2E] p-4">
              <div>
                <p className="font-medium">Analytics</p>
                <p className="text-sm text-[#A1A1AA]">Helps us understand how the site is used.</p>
              </div>
              <Switch checked={analytics} onCheckedChange={setAnalytics} aria-label="Analytics cookies" data-testid="cookie-analytics-switch" />
            </div>
          </div>
          <button onClick={() => save(analytics)} className="lc-btn-primary mt-2 w-full" data-testid="cookie-save">Save preferences</button>
        </DialogContent>
      </Dialog>
    </>
  );
};
